import React, { useState } from 'react';
import { XIcon, GripVerticalIcon } from 'lucide-react';
interface EditableChartGridProps {
  /** Full ordering of every chart, including hidden ones. */
  chartOrder: string[];
  /** Ids currently shown on the dashboard. */
  visibleCharts: Set<string>;
  /** When true, cards become draggable and expose a remove control. */
  isEditing: boolean;
  /** Receives the complete reordered chartOrder (hidden ids preserved). */
  onReorder: (chartOrder: string[]) => void;
  onRemove: (chartId: string) => void;
  renderChart: (chartId: string) => React.ReactNode;
}

/**
 * The "Your overview" chart grid. In view mode it renders charts normally; in
 * edit mode the same cards become drag-to-reorder targets with a remove
 * control, so editing happens in place rather than inside a modal.
 */
export function EditableChartGrid({
  chartOrder,
  visibleCharts,
  isEditing,
  onReorder,
  onRemove,
  renderChart
}: EditableChartGridProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const visibleOrder = chartOrder.filter(id => visibleCharts.has(id));

  // Reorder only the visible ids, then slot them back into the positions the
  // visible charts occupy in the full order. This keeps hidden charts parked
  // where they were instead of silently drifting.
  const commitReorder = (from: number, to: number) => {
    const nextVisible = [...visibleOrder];
    const [moved] = nextVisible.splice(from, 1);
    nextVisible.splice(to, 0, moved);
    let cursor = 0;
    onReorder(chartOrder.map(id => visibleCharts.has(id) ? nextVisible[cursor++] : id));
  };
  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      commitReorder(draggedIndex, dragOverIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Keyboard reordering so the grid isn't pointer-only.
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const to = e.key === 'ArrowLeft' ? index - 1 : index + 1;
    if (to < 0 || to >= visibleOrder.length) return;
    e.preventDefault();
    commitReorder(index, to);
  };
  if (visibleOrder.length === 0) {
    return <div className="flex flex-col items-center justify-center py-12 text-center border border-dashed border-gray-300 rounded-lg" data-id="element-1861" data-mp="1pky9qp2pghao">
        <p className="text-sm text-gray-500" data-id="element-1862" data-mp="1uj8adf778a3o">
          No charts to display. Use Add to put charts back on your overview.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1863" data-mp="23cbrbxh1z532">
      {visibleOrder.map((chartId, index) => {
      if (!isEditing) {
        return <React.Fragment key={chartId} data-id="element-1864" data-mp="28czkaooudvzx">{renderChart(chartId)}</React.Fragment>;
      }
      const isDragging = draggedIndex === index;
      const isDragOver = dragOverIndex === index && draggedIndex !== index;
      return <div key={chartId} draggable role="button" tabIndex={0} aria-label={`Reorder ${chartId.replace(/-/g, ' ')}. Use the left and right arrow keys to move it.`} onDragStart={() => setDraggedIndex(index)} onDragOver={e => {
        e.preventDefault();
        setDragOverIndex(index);
      }} onDragEnd={handleDragEnd} onKeyDown={e => handleKeyDown(e, index)} className={`relative rounded-lg cursor-grab active:cursor-grabbing transition-[transform,opacity,box-shadow] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${isDragging ? 'opacity-50 scale-[0.98]' : isDragOver ? 'ring-2 ring-blue-500' : 'ring-1 ring-dashed ring-gray-300'}`} data-id="element-1865" data-mp="2nc3faqbfqv5n" data-mpi={index}>
            <div className="absolute top-3 left-3 text-gray-400 bg-white rounded p-1 shadow-sm border border-gray-200 z-10" data-id="element-1866" data-mp="34mfoa90idklj">
              <GripVerticalIcon size={16} aria-hidden="true" data-id="element-1867" data-mp="39mlcff7kkoyv" />
            </div>
            <button type="button" onClick={() => onRemove(chartId)} aria-label={`Remove ${chartId.replace(/-/g, ' ')} chart`} className="absolute top-3 right-3 p-1 bg-white hover:bg-gray-100 rounded-full transition-colors duration-150 ease-out z-10 shadow-sm border border-gray-200" data-id="element-1868" data-mp="2vn2dnd0nepz8">
              <XIcon size={16} className="text-gray-500" aria-hidden="true" data-id="element-1869" data-mp="3se49l5ewx8sz" />
            </button>
            {/* Charts stay inert while editing so drags never hit chart internals. */}
            <div className="pointer-events-none" data-id="element-1870" data-mp="3ikdq6exn7ok5">{renderChart(chartId)}</div>
          </div>;
    })}
    </div>;
}